import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import {
  WebsiteRequest,
  WebsiteResponse,
  CheckSubdomainRequest,
  SaveCustomDomainResponse,
  CustomDomainDiagnosticResponse,
  VerifyCustomDomainResponse,
  ValidateCustomDomainResponse,
  DeleteCustomDomainResponse,
  AddPublicDocumentsResponse,
  ListedPublicFilesResponse,
  RemovePublicDocumentFilesResponse,
  CreatePageRequest,
  DeletePageResponse,
  Footer,
  FooterResponse,
  Header,
  HeaderResponse,
  NavItem,
  NavigationResponse,
  PageDetail,
  PageWithNavigationResponse,
  PreviewPageRequest,
  PreviewPageResponse,
  PreviewRegionRequest,
  PreviewRegionResponse,
  PublicDocument,
  PublicDocumentsResponse,
  PublishSummary,
  ReorderPagesResponse,
  SavePageBlocksRequest,
  SavePageBlocksResponse,
  Theme,
  ThemeResponse,
  UpdatePageSettingsRequest,
  VersionDetail,
  VersionPreviewResponse,
  VersionPreviewSide,
  VersionScope,
  VersionsResponse,
  WebsiteAmenitiesResponse,
  WebsiteAmenityOptions,
  WebsiteCalendarOption,
  ResidentPortalContent,
  ResidentPortalWidget,
  SharedList,
  SharedListKey,
  SharedLists,
} from '../../models/website-builder';

const SHARED_LIST_PATHS: Record<SharedListKey, string> = { amenities: 'amenities', boardMembers: 'board-members', faq: 'faq' };
import { HttpErrorResponse } from '@angular/common/http';

function scopeBody(scope: VersionScope, pageId: number | null): { scope?: VersionScope; pageId?: number } {
  if (scope === 'all') {
    return {};
  }

  return scope === 'page' && pageId ? { scope, pageId } : { scope };
}

function scopeQuery(scope: VersionScope, pageId: number | null, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams();
  if (scope !== 'all') {
    params.set('scope', scope);
  }
  if (pageId) {
    params.set('pageId', String(pageId));
  }
  Object.entries(extra).forEach(([key, value]) => params.set(key, value));
  const query = params.toString();

  return query ? `?${query}` : '';
}


/** The library file an upload created; the id may sit inside `data` or at the top level. */
export interface UploadDocumentResponse {
  success?: boolean;
  message?: string;
  data?: { id: number };
  id?: number;
}

type Wrapped<T> = T | { data: T };

/** New endpoints may answer bare or inside { success, message, data }. */
function unwrap<T>(response: Wrapped<T>): T {
  const wrapped = response as { data?: T } | null;

  return (wrapped?.data ?? response) as T;
}

@Injectable({
  providedIn: 'root'
})
export class WebsiteBuilderService {
  constructor(private http: ApplicationHttpClient) { }

  /**
   * Lightweight check if AI chatbot is enabled (for sidebar navigation)
   * Returns only boolean, not full website object
   */
  checkAiChatbotEnabled(): Observable<boolean> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = `${Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId)}/ai-chatbot-status`;

    return this.http.get<{ enabled: boolean }>(url).pipe(
      map(response => response.enabled),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error checking AI chatbot status:', error);
        return of(false); // Default to false if error
      })
    );
  }

  /**
   * Get the website for the current organization
   */
  getWebsite(): Observable<WebsiteResponse | null> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId);

    return this.http.get<WebsiteResponse>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error fetching website:', error);
        if (error.status === 404) {
          return of(null);
        }
        throw error;
      })
    );
  }


  /**
   * Create a new website
   */
  createWebsite(data: WebsiteRequest): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId);
    return this.http.post<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Update an existing website
   */
  updateWebsite(id: number, data: Partial<WebsiteRequest>): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.WebsiteById(id), jwt.orgId);

    return this.http.put<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Publish the website
   */
  publishWebsite(id: number): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.PublishWebsite(id), jwt.orgId);
    return this.http.post<WebsiteResponse>(url, JSON.stringify({}));
  }

  /**
   * Check if subdomain is available
   */
  checkSubdomainAvailability(subdomain: string): Observable<{ available: boolean }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.CheckSubdomain(), jwt.orgId);
    const data: CheckSubdomainRequest = { subdomain };
    return this.http.post<{ available: boolean }>(url, JSON.stringify(data));
  }

  /**
   * Check if custom domain is available (not used by another website)
   */
  checkCustomDomainAvailability(domain: string): Observable<ValidateCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/validate', jwt.orgId);
    const data = { domain };
    return this.http.post<ValidateCustomDomainResponse>(url, JSON.stringify(data))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Custom domain validation failed:', error);
          // On error, don't block the user - let them continue
          return of({ available: true, message: 'Could not validate domain availability' });
        })
      );
  }

  /**
   * Save custom domain to website
   */
  saveCustomDomain(domain: string): Observable<SaveCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/save', jwt.orgId);
    const data = { domain };
    return this.http.post<SaveCustomDomainResponse>(url, JSON.stringify(data));
  }

  /**
   * Delete custom domain from website
   */
  deleteCustomDomain(): Observable<DeleteCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/delete', jwt.orgId);
    return this.http.post<DeleteCustomDomainResponse>(url, JSON.stringify({}));
  }

  /**
   * Verify custom domain DNS configuration
   */
  verifyCustomDomain(): Observable<VerifyCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/verify', jwt.orgId);
    return this.http.post<VerifyCustomDomainResponse>(url, JSON.stringify({}));
  }

  /**
   * Super-only read-only diagnostic of the custom domain
   */
  diagnoseCustomDomain(): Observable<CustomDomainDiagnosticResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/diagnose', jwt.orgId);
    return this.http.get<CustomDomainDiagnosticResponse>(url);
  }

  /**
   * Get SSL validation records for existing certificate (does NOT request new certificate)
   */
  getSslValidationRecords(): Observable<SaveCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/ssl-validation-records', jwt.orgId);
    return this.http.get<SaveCustomDomainResponse>(url);
  }

  /**
   * Upload images for website content
   */
  uploadImage(file: File, section: string): Observable<{ url: string }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/upload-image', jwt.orgId);
    const formData = new FormData();
    formData.append('image', file);
    formData.append('section_type', section);  // Fixed to match backend expectation
    return this.http.http.post<{ url: string }>(url, formData);
  }

  /**
   * Save comprehensive website data including sections
   */
  saveWebsite(data: Partial<WebsiteRequest>): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save', jwt.orgId);

    return this.http.post<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Unified save for general settings + sections (AI chatbot stays separate)
   * Used for: draft saves, tab switching auto-save, and preview (with preview=true flag)
   * AI chatbot disabled by default for new customers - separate workflow
   */
  saveGeneralAndSections(data: Partial<WebsiteRequest>, preview: boolean = false): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save', jwt.orgId);

    const requestData = {
      ...data,
      preview: preview  // Only set preview flag if requested
    };


    return this.http.post<any>(url, JSON.stringify(requestData)).pipe(
      map(response => {
        // UNIFIED RESPONSE HANDLING: Both preview and normal saves now return the same structure
        // API always returns { success, message, data } format
        return response.data ? response.data as WebsiteResponse : response as WebsiteResponse;
      })
    );
  }

  /**
   * Save AI chatbot settings
   */
  saveAIChatbotSettings(data: { enabled: boolean; linkedDocuments: any[] }): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save-ai-chatbot', jwt.orgId);

    const requestData = {
      ai_chatbot: {
        enabled: data.enabled,
        linkedDocuments: data.linkedDocuments
      }
    };

    return this.http.post<any>(url, JSON.stringify(requestData)).pipe(
      map(response => {
        // API returns { success, message, data } format
        return response.data as WebsiteResponse;
      })
    );
  }

  /**
   * Remove a specific document from AI chatbot settings (real-time operation)
   */
  removeAIChatbotDocument(documentId: number): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/document/${documentId}`);

    return this.http.delete<{ success: boolean; message: string; data: any }>(url);
  }

  /**
   * Clear all document memories from AI chatbot (pgvector database)
   * This removes all learned content from uploaded documents
   */
  clearAllAIChatbotDocumentMemories(): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/clear-memories`);

    return this.http.delete<{ success: boolean; message: string; data: any }>(url);
  }

  /**
   * Add new documents to AI chatbot settings (real-time operation)
   */
  addAIChatbotDocuments(documents: any[]): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/documents`);

    const requestData = {
      documents: documents
    };

    return this.http.post<{ success: boolean; message: string; data: any }>(url, JSON.stringify(requestData));
  }

  /**
   * Upload a document file to organization storage (creates OrganizationFile record)
   */
  uploadDocumentFile(file: File): Observable<UploadDocumentResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/upload-document', jwt.orgId);

    const formData = new FormData();
    formData.append('document', file);

    return this.http.http.post<UploadDocumentResponse>(url, formData);
  }


  /**
   * Start building website - changes status from 'initialized' to 'draft'
   * ONLY controlled way to transition from never-started to actively-building
   */
  startBuilding(websiteId: number): Observable<{ success: boolean; message: string; status: string }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = `${Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId)}/${websiteId}/start-building`;

    return this.http.patch<{ success: boolean; message: string; status: string }>(url, null);
  }

  getPage(pageId: number): Observable<PageDetail> {
    return this.http.get<Wrapped<PageDetail>>(this.moduleUrl(Routes.WebsiteBuilderModule.Page(pageId)))
      .pipe(map(response => unwrap<PageDetail>(response)));
  }

  createPage(data: CreatePageRequest): Observable<PageWithNavigationResponse> {
    return this.http.post<Wrapped<PageWithNavigationResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Pages()), JSON.stringify(data))
      .pipe(map(response => unwrap<PageWithNavigationResponse>(response)));
  }

  updatePageSettings(pageId: number, data: UpdatePageSettingsRequest): Observable<PageWithNavigationResponse> {
    return this.http.patch<Wrapped<PageWithNavigationResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Page(pageId)), JSON.stringify(data))
      .pipe(map(response => unwrap<PageWithNavigationResponse>(response)));
  }

  savePageBlocks(pageId: number, data: SavePageBlocksRequest): Observable<SavePageBlocksResponse> {
    return this.http.put<Wrapped<SavePageBlocksResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PageBlocks(pageId)), JSON.stringify(data))
      .pipe(map(response => unwrap<SavePageBlocksResponse>(response)));
  }

  previewPage(pageId: number, draft: PreviewPageRequest): Observable<PreviewPageResponse> {
    return this.http.post<Wrapped<PreviewPageResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PagePreview(pageId)), JSON.stringify(draft))
      .pipe(map(response => unwrap<PreviewPageResponse>(response)));
  }

  previewRegion(draft: PreviewRegionRequest): Observable<PreviewRegionResponse> {
    return this.http.post<Wrapped<PreviewRegionResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.RegionPreview()), JSON.stringify(draft))
      .pipe(map(response => unwrap<PreviewRegionResponse>(response)));
  }

  duplicatePage(pageId: number): Observable<PageWithNavigationResponse> {
    return this.http.post<Wrapped<PageWithNavigationResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.DuplicatePage(pageId)), JSON.stringify({}))
      .pipe(map(response => unwrap<PageWithNavigationResponse>(response)));
  }

  deletePage(pageId: number): Observable<DeletePageResponse> {
    return this.http.delete<Wrapped<DeletePageResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Page(pageId)))
      .pipe(map(response => unwrap<DeletePageResponse>(response)));
  }

  reorderPages(pageIds: number[]): Observable<ReorderPagesResponse> {
    return this.http.post<Wrapped<ReorderPagesResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.ReorderPages()), JSON.stringify({ pageIds }))
      .pipe(map(response => unwrap<ReorderPagesResponse>(response)));
  }

  saveNavigation(items: NavItem[], navigationRevision: number): Observable<NavigationResponse> {
    return this.http.put<Wrapped<NavigationResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Navigation()), JSON.stringify({ items, navigationRevision }))
      .pipe(map(response => unwrap<NavigationResponse>(response)));
  }

  saveHeader(header: Header, headerRevision: number): Observable<HeaderResponse> {
    return this.http.put<Wrapped<HeaderResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Header()), JSON.stringify({ header, headerRevision }))
      .pipe(map(response => unwrap<HeaderResponse>(response)));
  }

  saveTheme(theme: Theme, themeRevision: number): Observable<ThemeResponse> {
    return this.http.put<Wrapped<ThemeResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Theme()), JSON.stringify({ theme, themeRevision }))
      .pipe(map(response => unwrap<ThemeResponse>(response)));
  }

  saveFooter(footer: Footer, footerRevision: number): Observable<FooterResponse> {
    return this.http.put<Wrapped<FooterResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Footer()), JSON.stringify({ footer, footerRevision }))
      .pipe(map(response => unwrap<FooterResponse>(response)));
  }

  getPublicDocuments(): Observable<PublicDocumentsResponse> {
    return this.http.get<Wrapped<PublicDocumentsResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublicDocuments()))
      .pipe(map(response => unwrap<PublicDocumentsResponse>(response)));
  }

  addPublicDocuments(fileIds: number[]): Observable<AddPublicDocumentsResponse> {
    return this.http.post<Wrapped<AddPublicDocumentsResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublicDocuments()), JSON.stringify({ fileIds }))
      .pipe(map(response => unwrap<AddPublicDocumentsResponse>(response)));
  }

  updatePublicDocument(id: number, data: { title?: string; note?: string | null }): Observable<PublicDocument> {
    return this.http.patch<Wrapped<PublicDocument>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublicDocument(id)), JSON.stringify(data))
      .pipe(map(response => unwrap<PublicDocument>(response)));
  }

  reorderPublicDocuments(ids: number[]): Observable<PublicDocumentsResponse> {
    return this.http.post<Wrapped<PublicDocumentsResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.ReorderPublicDocuments()), JSON.stringify({ ids }))
      .pipe(map(response => unwrap<PublicDocumentsResponse>(response)));
  }

  getListedPublicFiles(): Observable<ListedPublicFilesResponse> {
    return this.http.get<Wrapped<ListedPublicFilesResponse>>(this.moduleUrl(`${Routes.WebsiteBuilderModule.PublicDocuments()}/files`))
      .pipe(map(response => unwrap<ListedPublicFilesResponse>(response)));
  }

  removePublicDocumentFiles(fileIds: number[]): Observable<RemovePublicDocumentFilesResponse> {
    return this.http.delete<Wrapped<RemovePublicDocumentFilesResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublicDocuments()), { body: { fileIds } })
      .pipe(map(response => unwrap<RemovePublicDocumentFilesResponse>(response)));
  }

  removePublicDocument(id: number): Observable<PublicDocumentsResponse> {
    return this.http.delete<Wrapped<PublicDocumentsResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublicDocument(id)))
      .pipe(map(response => unwrap<PublicDocumentsResponse>(response)));
  }

  getWebsiteAmenities(): Observable<WebsiteAmenitiesResponse> {
    return this.http.get<Wrapped<WebsiteAmenitiesResponse>>(this.moduleUrl('/amenities'))
      .pipe(map(response => unwrap<WebsiteAmenitiesResponse>(response)));
  }

  getWebsiteCalendars(orgId: number): Observable<WebsiteCalendarOption[]> {
    return this.http.get<Wrapped<WebsiteCalendarOption[]>>(Routes.MakeWebsiteBuilderModuleUrl('/calendars', orgId))
      .pipe(map(response => unwrap<WebsiteCalendarOption[]>(response)));
  }

  saveWebsiteAmenities(options: Partial<WebsiteAmenityOptions>): Observable<WebsiteAmenitiesResponse> {
    return this.http.put<Wrapped<WebsiteAmenitiesResponse>>(this.moduleUrl('/amenities'), JSON.stringify(options))
      .pipe(map(response => unwrap<WebsiteAmenitiesResponse>(response)));
  }

  getSharedLists(): Observable<SharedLists> {
    return this.http.get<Wrapped<SharedLists>>(this.moduleUrl('/widgets'))
      .pipe(map(response => unwrap<SharedLists>(response)));
  }

  saveSharedList<K extends SharedListKey>(list: K, items: SharedList<K>['items'], revision: number): Observable<SharedList<K>> {
    return this.http.put<Wrapped<SharedList<K>>>(this.moduleUrl(`/widgets/${SHARED_LIST_PATHS[list]}`), JSON.stringify({ items, revision }))
      .pipe(map(response => unwrap<SharedList<K>>(response)));
  }

  saveResidentPortal(content: ResidentPortalContent, revision: number): Observable<ResidentPortalWidget> {
    return this.http.put<Wrapped<ResidentPortalWidget>>(this.moduleUrl('/widgets/resident-portal'), JSON.stringify({ ...content, revision }))
      .pipe(map(response => unwrap<ResidentPortalWidget>(response)));
  }

  getPublishSummary(): Observable<PublishSummary> {
    return this.http.get<Wrapped<PublishSummary>>(this.moduleUrl(Routes.WebsiteBuilderModule.PublishSummary()))
      .pipe(map(response => unwrap<PublishSummary>(response)));
  }

  getVersions(scope: VersionScope = 'all', pageId: number | null = null): Observable<VersionsResponse> {
    return this.http.get<Wrapped<VersionsResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Versions() + scopeQuery(scope, pageId)))
      .pipe(map(response => unwrap<VersionsResponse>(response)));
  }

  getVersion(id: number, scope: VersionScope = 'all', pageId: number | null = null): Observable<VersionDetail> {
    return this.http.get<Wrapped<VersionDetail>>(this.moduleUrl(Routes.WebsiteBuilderModule.Version(id) + scopeQuery(scope, pageId)))
      .pipe(map(response => unwrap<VersionDetail>(response)));
  }

  getVersionPreview(id: number, pageId: number | null, scope: VersionScope = 'all', side: VersionPreviewSide = 'version'): Observable<VersionPreviewResponse> {
    const query = scopeQuery(scope, pageId, { side });

    return this.http.get<Wrapped<VersionPreviewResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.VersionPreview(id) + query))
      .pipe(map(response => unwrap<VersionPreviewResponse>(response)));
  }

  /** Copies one part of a version into the draft; the live site does not change. */
  restoreVersion(id: number, scope: VersionScope = 'all', pageId: number | null = null): Observable<WebsiteResponse> {
    return this.http.post<Wrapped<WebsiteResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.RestoreVersion(id)), JSON.stringify(scopeBody(scope, pageId)))
      .pipe(map(response => unwrap<WebsiteResponse>(response)));
  }

  /** Throws away draft changes to one part of the site; no scope is the whole site. */
  discardChanges(scope: VersionScope = 'all', pageId: number | null = null): Observable<WebsiteResponse> {
    return this.http.post<Wrapped<WebsiteResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.Discard()), JSON.stringify(scopeBody(scope, pageId)))
      .pipe(map(response => unwrap<WebsiteResponse>(response)));
  }

  startOver(name?: string): Observable<WebsiteResponse> {
    const body = name?.trim() ? { name: name.trim() } : {};

    return this.http.post<Wrapped<WebsiteResponse>>(this.moduleUrl(Routes.WebsiteBuilderModule.StartOver()), JSON.stringify(body))
      .pipe(map(response => unwrap<WebsiteResponse>(response)));
  }

  private moduleUrl(path: string): string {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();

    return Routes.MakeWebsiteBuilderModuleUrl(path, jwt.orgId);
  }

}
